import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supabase } from '../../services/supabase';

// 类型定义
export interface ChatMessage {
  id: string;
  group_id: string;
  user_id: string;
  message: string;  // 改为 message 字段，与数据库一致
  message_type: 'text' | 'image' | 'system';
  created_at: string;
  updated_at: string;
  user_profile?: {
    display_name: string | null;
    avatar_url: string | null;
  };
}

export interface ChatState {
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  currentGroupId: string | null;
}

const initialState: ChatState = {
  messages: [],
  loading: false,
  error: null,
  currentGroupId: null,
};

// 异步操作：获取群聊消息
export const getChatMessages = createAsyncThunk(
  'chat/getChatMessages',
  async (groupId: string, { rejectWithValue }) => {
    try {
      // 先获取消息
      const { data: messagesData, error: messagesError } = await supabase
        .from('group_messages')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: true })
        .limit(100);

      if (messagesError) throw messagesError;

      // 为每条消息获取用户资料
      const messages = await Promise.all(
        (messagesData || []).map(async (message: any) => {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('display_name, avatar_url')
            .eq('id', message.user_id)
            .single();

          return {
            ...message,
            user_profile: profileData || { display_name: null, avatar_url: null }
          };
        })
      );
      
      return { groupId, messages: messages as ChatMessage[] };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：发送消息
export const sendChatMessage = createAsyncThunk(
  'chat/sendChatMessage',
  async (
    params: { 
      groupId: string; 
      message: string;
      messageType?: 'text' | 'image' | 'system' 
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const { groupId, message, messageType = 'text' } = params;
      const state = getState() as any;
      const currentUser = state.auth.user;
      
      if (!currentUser) {
        throw new Error('用户未登录');
      }

      // 确保 message 是字符串类型
      const messageText = String(message).trim();
      
      if (!messageText) {
        throw new Error('消息内容不能为空');
      }

      // 先插入消息
      const { data: messageData, error: insertError } = await supabase
        .from('group_messages')
        .insert({
          group_id: groupId,
          user_id: currentUser.id,
          message: messageText,
          message_type: messageType,
        })
        .select('*')
        .single();

      if (insertError) throw insertError;

      // 获取用户资料
      const { data: profileData } = await supabase
        .from('profiles')
        .select('display_name, avatar_url')
        .eq('id', currentUser.id)
        .single();

      // 合并数据
      const result = {
        ...messageData,
        user_profile: profileData || { display_name: null, avatar_url: null }
      };
      
      return result as ChatMessage;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：删除消息（仅消息发送者或群组管理员可删除）
export const deleteChatMessage = createAsyncThunk(
  'chat/deleteChatMessage',
  async (messageId: string, { rejectWithValue }) => {
    try {
      const { error } = await supabase
        .from('group_messages')
        .delete()
        .eq('id', messageId);

      if (error) throw error;
      return messageId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearChatMessages: (state) => {
      state.messages = [];
      state.currentGroupId = null;
      state.error = null;
    },
    addRealTimeMessage: (state, action: PayloadAction<ChatMessage>) => {
      // 避免重复添加消息
      if (!state.messages.find(msg => msg.id === action.payload.id)) {
        state.messages.push(action.payload);
      }
    },
    removeRealTimeMessage: (state, action: PayloadAction<string>) => {
      state.messages = state.messages.filter(msg => msg.id !== action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // getChatMessages
      .addCase(getChatMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getChatMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.currentGroupId = action.payload.groupId;
        state.messages = action.payload.messages;
      })
      .addCase(getChatMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // sendChatMessage
      .addCase(sendChatMessage.pending, (state) => {
        state.error = null;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        // 避免重复添加消息（因为可能有实时订阅）
        if (!state.messages.find(msg => msg.id === action.payload.id)) {
          state.messages.push(action.payload);
        }
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // deleteChatMessage
      .addCase(deleteChatMessage.fulfilled, (state, action) => {
        state.messages = state.messages.filter(msg => msg.id !== action.payload);
      })
      .addCase(deleteChatMessage.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearChatMessages, 
  addRealTimeMessage, 
  removeRealTimeMessage, 
  clearError 
} = chatSlice.actions;

export default chatSlice.reducer;
